/**
 *
 * Evaluates field-based suggestions from Benny suggestions API.
 */

export type FieldValue = string | number | boolean | null;

export interface RulePredicate {
  condition: string;
  dependsOnField: string | null;
  dependsOnValue: FieldValue;
}

export interface FieldDependencyRule {
  field: string;
  value: FieldValue;
  // Predicates, all ANDed together. Empty/absent means the rule always applies.
  conditions?: RulePredicate[];
  note: string | null;
}

export class FieldDependencyManager {
  private rules: FieldDependencyRule[];

  constructor(rules: FieldDependencyRule[]) {
    this.rules = rules;
  }

  // Mirrors the backend apply_operator (suggestions.py).
  private static evaluatePredicate(
    predicate: RulePredicate,
    formValues: Record<string, FieldValue>,
  ): boolean {
    const op = predicate.condition;
    if (!op || op === "always") return true;
    const { dependsOnField } = predicate;
    if (!dependsOnField) return false;
    const raw = formValues[dependsOnField];
    // A cleared form select surfaces "", which the operators must read as unset.
    const actual = typeof raw === "string" && raw.trim() === "" ? null : raw;
    const { dependsOnValue } = predicate;
    if (op === "is_null") return actual === null || actual === undefined;
    if (op === "is_not_null") return actual !== null && actual !== undefined;
    if (actual === null || actual === undefined) return false;
    if (op === "eq") return actual === dependsOnValue;
    if (op === "ne") return actual !== dependsOnValue;
    const left = Number(actual);
    const right = Number(dependsOnValue);
    if (Number.isNaN(left) || Number.isNaN(right)) return false;
    if (op === "gt") return left > right;
    if (op === "gte") return left >= right;
    if (op === "lt") return left < right;
    if (op === "lte") return left <= right;
    return false;
  }

  private static evaluateCondition(
    rule: FieldDependencyRule,
    formValues: Record<string, FieldValue>,
  ): boolean {
    return (rule.conditions ?? []).every((p) =>
      FieldDependencyManager.evaluatePredicate(p, formValues),
    );
  }

  getSuggestions(
    formValues: Record<string, FieldValue>,
  ): Record<string, FieldValue> {
    return this.rules.reduce(
      (suggestions, rule) => {
        if (FieldDependencyManager.evaluateCondition(rule, formValues)) {
          return Object.assign(suggestions, { [rule.field]: rule.value });
        }
        return suggestions;
      },
      {} as Record<string, FieldValue>,
    );
  }

  getMatchingRules(
    formValues: Record<string, FieldValue>,
  ): FieldDependencyRule[] {
    return this.rules.filter((rule) =>
      FieldDependencyManager.evaluateCondition(rule, formValues),
    );
  }

  getSuggestionForField(
    fieldName: string,
    formValues: Record<string, FieldValue>,
  ): FieldValue | undefined {
    const rule = this.rules.find(
      (r) =>
        r.field === fieldName &&
        FieldDependencyManager.evaluateCondition(r, formValues),
    );
    return rule?.value;
  }

  getDependentField(fieldName: string): string | undefined {
    const rule = this.rules.find((r) => r.field === fieldName);
    return rule?.conditions?.[0]?.dependsOnField ?? undefined;
  }

  // Every form field these rules read, so callers know what to re-evaluate on.
  getDependencyFields(): string[] {
    return Array.from(
      new Set(
        this.rules
          .flatMap((r) => (r.conditions ?? []).map((p) => p.dependsOnField))
          .filter((f): f is string => Boolean(f)),
      ),
    );
  }
}
